import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AlertTriangle, ExternalLink, RefreshCw, X } from "lucide-react";
import { useState } from "react";

export interface PersistentErrorBannerProps {
  title: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** Disables the retry button and spins its icon while a retry is in flight. */
  retryPending?: boolean;
  supportLink?: string;
  supportLabel?: string;
  onDismiss?: () => void;
  className?: string;
}

export function PersistentErrorBanner({
  title,
  description,
  onRetry,
  retryLabel,
  retryPending = false,
  supportLink,
  supportLabel,
  onDismiss,
  className,
}: PersistentErrorBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div
      className={cn(
        "border border-destructive/50 rounded-lg p-4 bg-destructive/5 animate-in fade-in slide-in-from-top-2 duration-300",
        className,
      )}
      role="alert"
      data-testid="persistent-error-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="mt-0.5 shrink-0">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div className="min-w-0 space-y-2">
            <p className="text-sm font-semibold" data-testid="text-error-banner-title">{title}</p>
            {description && (
              <p className="text-xs text-muted-foreground" data-testid="text-error-banner-description">{description}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {onRetry && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRetry}
                  disabled={retryPending}
                  data-testid="button-error-retry"
                >
                  <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", retryPending && "animate-spin")} />
                  {retryLabel || "Retry"}
                </Button>
              )}
              {supportLink && (
                <a
                  href={supportLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="link-error-support"
                >
                  <ExternalLink className="h-3 w-3" />
                  {supportLabel || "Contact support"}
                </a>
              )}
            </div>
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={() => {
              setDismissed(true);
              onDismiss();
            }}
            className="text-muted-foreground hover:text-foreground shrink-0"
            data-testid="button-dismiss-error-banner"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
